/**
 * @file MlrRulebookPanel.tsx
 * @brief Super Admin → the rulebook. What DECIDES what goes in a report.
 *
 * @details
 * The instructions on this page shape how a section is WRITTEN. This decides
 * what applies to it — whether the Whiplash Injury Regulations 2021 cover an
 * injury, and so which tariff it falls under.
 *
 * ⚠️ The two are kept apart because they must be. Instructions are prose
 * appended to an extractor's prompt, and prose in a prompt does not hold:
 * measured on four separate house instructions, a contrary instruction broke
 * each one **3 runs out of 3**. Rules are data the code evaluates, which is
 * why they can be relied on and why they look like this rather than like a
 * paragraph.
 *
 * ## Preview before publish, always
 *
 * A wrong rule is wrong on every report from the moment it lands, quietly, in
 * a field that decides money. The preview runs a draft against real past cases
 * and shows what changes. It writes nothing.
 *
 * ## Three outcomes
 *
 * A rule either applies, is known not to, or cannot tell — and the third is an
 * answer, not a failure. `otherwise` fires only on a KNOWN false; a missing
 * fact leaves the decision open and the doctor is asked once, on the report.
 *
 * @references
 * - BACKEND/src/features/medical-assessment/rulebook/
 * - docs/product/medical-report-rulebook-plan.md
 * @author DES
 * @date 18-09-2026
 */

import { SkeletonFields } from "@/components/ui/Skeleton";
import { useEffect, useMemo, useState } from "react";
import { CircleAlert, LoaderCircle, Play, Save } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { toast } from "@/shared/hooks/useToast";
import {
  useMlrRulebook,
  usePreviewRulebook,
  usePublishRulebook,
} from "./api";
import type { MlrRule } from "./schemas";

function stringifyRules(rules: MlrRule[]): string {
  return JSON.stringify(rules, null, 2);
}

export function MlrRulebookPanel() {
  const rulebook = useMlrRulebook();
  const preview = usePreviewRulebook();
  const publish = usePublishRulebook();

  const [draft, setDraft] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [loadedVersion, setLoadedVersion] = useState<number | null | undefined>(
    undefined,
  );

  const live = rulebook.data?.rulebook;

  // Load once per published version, so an edit in progress is not overwritten
  // by a refetch that returned the same rules.
  useEffect(() => {
    if (!live) return;
    if (loadedVersion === live.version) return;
    setDraft(stringifyRules(live.rules));
    setLoadedVersion(live.version);
  }, [live, loadedVersion]);

  const parsed = useMemo<{ rules: MlrRule[] | null; error: string | null }>(() => {
    if (!draft.trim()) return { rules: [], error: null };
    try {
      const value = JSON.parse(draft);
      if (!Array.isArray(value)) {
        return { rules: null, error: "The rulebook must be a list of rules." };
      }
      return { rules: value as MlrRule[], error: null };
    } catch (err) {
      return {
        rules: null,
        error: err instanceof Error ? err.message : "Could not read that.",
      };
    }
  }, [draft]);

  useEffect(() => setParseError(parsed.error), [parsed.error]);

  const dirty = live ? draft !== stringifyRules(live.rules) : false;
  const result = preview.data;

  const onPreview = async () => {
    if (!parsed.rules) return;
    try {
      await preview.mutateAsync({ rules: parsed.rules });
    } catch (err) {
      toast({
        title: "Could not run the preview",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    }
  };

  const onPublish = async () => {
    if (!parsed.rules) return;

    // The preview is the only thing standing between a typo and every report,
    // so publishing without one is refused rather than discouraged.
    if (!result || preview.isPending) {
      toast({
        title: "Run the preview first",
        description:
          "A rule applies to every report from the moment it is published. See what it changes before it does.",
        variant: "destructive",
      });
      return;
    }

    const ok = window.confirm(
      `Publish this rulebook?\n\n` +
        `It decides classifications on every report generated from now on. ` +
        `The preview found ${result.changed.length} change(s) across ` +
        `${result.injuries} injuries in ${result.sampled} recent cases.`,
    );
    if (!ok) return;

    try {
      await publish.mutateAsync({ rules: parsed.rules });
      toast({ title: "Rulebook published" });
      setLoadedVersion(undefined);
    } catch (err) {
      toast({
        title: "Could not publish",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    }
  };

  if (rulebook.isLoading) {
    return (
      <SkeletonFields fields={4} className="p-6" />
    );
  }

  return (
    <div className="space-y-4 p-1">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex-1 text-base font-semibold tracking-tight">
          Rulebook
        </h3>
        {live?.source === "code" ? (
          <Badge variant="secondary">Built-in rules — nothing published yet</Badge>
        ) : (
          <Badge variant="secondary">Version {live?.version}</Badge>
        )}
        {dirty ? (
          <Badge variant="outline" className="text-destructive">
            Unpublished changes
          </Badge>
        ) : null}
      </div>

      <p className="text-sm text-muted-foreground">
        Instructions shape how a section is <em>written</em>. These decide what{" "}
        <em>applies</em> — whether an injury falls under the whiplash tariff
        regulations, for instance. A rule either applies, is known not to, or
        cannot tell; the third asks the doctor once rather than guessing.
      </p>

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        rows={18}
        className="w-full rounded-md border bg-field p-3 font-mono text-xs"
        aria-label="Rulebook"
      />

      {parseError ? (
        <p className="flex items-start gap-1.5 text-sm text-destructive">
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          {parseError}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={!parsed.rules || preview.isPending}
          onClick={() => void onPreview()}
        >
          {preview.isPending ? (
            <LoaderCircle className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Play className="mr-1.5 h-4 w-4" />
          )}
          Preview against recent cases
        </Button>
        <Button
          type="button"
          disabled={!parsed.rules || publish.isPending || !dirty}
          onClick={() => void onPublish()}
        >
          <Save className="mr-1.5 h-4 w-4" />
          Publish
        </Button>
      </div>

      {result ? (
        <div className="rounded-md border p-3 text-sm">
          {result.problems.length ? (
            <>
              <p className="font-semibold text-destructive">
                These rules cannot be published
              </p>
              <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                {result.problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p className="font-semibold">
                {result.changed.length} change
                {result.changed.length === 1 ? "" : "s"} across{" "}
                {result.injuries} injuries in {result.sampled} recent cases
              </p>

              {Object.entries(result.summary).map(([decision, counts]) => (
                <p key={decision} className="mt-1 text-muted-foreground">
                  {decision}: {counts.applies} applies, {counts.notApplicable}{" "}
                  not applicable, {counts.undetermined} would ask the doctor
                </p>
              ))}

              {result.changed.length ? (
                <ul className="mt-2 space-y-1">
                  {result.changed.map((row, index) => (
                    <li
                      key={`${row.assessmentId}-${row.injury}-${index}`}
                      className="text-muted-foreground"
                    >
                      <span className="font-medium text-foreground">
                        {row.claimant}
                      </span>{" "}
                      — {row.injury}: {row.was ?? "undetermined"} →{" "}
                      <span className="font-medium text-foreground">
                        {row.now ?? "undetermined"}
                      </span>
                      {row.nowRule ? ` (${row.nowRule})` : ""}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-muted-foreground">
                  Nothing would change on these cases.
                </p>
              )}
            </>
          )}
        </div>
      ) : null}

      {rulebook.data?.factPaths?.length ? (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">
            Facts a rule may test ({rulebook.data.factPaths.length})
          </summary>
          <p className="mt-1">
            A rule naming anything else is refused — a renamed field has to fail
            loudly, not stop matching in silence.
          </p>
          <p className="mt-1 font-mono">
            {rulebook.data.factPaths.join("  ·  ")}
          </p>
        </details>
      ) : null}
    </div>
  );
}
